export type CoordinationOwner = {
  id: string;
  name: string;
};

export type CoordinationExternalUser = {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  company?: string | null;
  notes?: string | null;
  active: boolean;
};

export type CoordinationPlanningInternalUser = {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  title?: string | null;
  email: string;
  phone?: string | null;
  role: string;
  avatarUrl?: string | null;
};

export type CoordinationPlanningMedicalForm = {
  id: string;
  code: string;
  name: string;
  category?: string | null;
  estimatedMinutes?: number | null;
};

export type CoordinationTaskCatalogEntry = {
  code: string;
  category: string;
  title: string;
  description: string;
  suggestedRole: string | null;
  suggestedPriority: string | null;
  defaultRecurrence: 'ONE_TIME' | 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'CUSTOM_DAYS';
};

export type CoordinationTaskMetadata = {
  assignee?: {
    type: 'internal' | 'external' | 'unassigned';
    internalUserId?: string | null;
    externalUserId?: string | null;
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    company?: string | null;
  };
  recurrence?: {
    type: 'ONE_TIME' | 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'CUSTOM_DAYS';
    every?: number | null;
    description?: string | null;
  };
  medicalForms?: Array<{
    templateId: string;
    name: string;
    code?: string | null;
  }>;
  automation?: {
    mode: 'MANUAL' | 'ON_TASK_DATE' | 'DAYS_BEFORE' | 'DAYS_AFTER';
    offsetDays?: number | null;
    description?: string | null;
  };
  template?: {
    code?: string | null;
    category?: string | null;
  };
  [key: string]: unknown;
};

export type CoordinationSummary = {
  activeEpisodeId: string | null;
  coordinator: { id?: string; name: string } | null;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  openTasks: number;
  overdueTasks: number;
  unresolvedAlerts: number;
  pendingReferrals: number;
  nextTouchpoint: string | null;
  lastPatientContact: string | null;
  lastHandoff: string | null;
};

export type CoordinationEpisode = {
  id: string;
  type: string;
  reason: string;
  severity: string | null;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  status: 'ACTIVE' | 'ON_HOLD' | 'CLOSED' | 'CANCELLED';
  coordinatorId: string | null;
  coordinator?: CoordinationOwner | null;
  startDate: string;
  expectedEndDate: string | null;
  closedAt: string | null;
  closureReason: string | null;
  outcomesSummary: string | null;
  unresolvedItemsDisposition: string | null;
  followUpPlan: string | null;
  patientStatus: string | null;
  reviewCadenceDays: number | null;
  nextReviewAt: string | null;
};

export type CoordinationTeamMember = {
  id: string;
  episodeId: string | null;
  actorType: 'PERSON' | 'ORGANIZATION' | 'CAREGIVER';
  actorName: string;
  role: string;
  function: string | null;
  organizationName: string | null;
  contact: string | null;
  contactPreference: string | null;
  accessLevel: string | null;
  responsibility: string | null;
  isCoordinator: boolean;
  startDate: string | null;
  endDate: string | null;
};

export type CoordinationPlan = {
  id: string;
  title: string;
  status: string;
  priority: string | null;
  diagnosis: string | null;
  category: string | null;
  progressPercent: number;
  adherencePercent: number | null;
  startDate: string | null;
  targetEndDate: string | null;
  nextReviewAt: string | null;
  updatedAt: string;
  episodeId: string | null;
};

export type CoordinationTask = {
  id: string;
  episodeId: string | null;
  title: string;
  description: string | null;
  status: 'NEW' | 'ASSIGNED' | 'IN_PROGRESS' | 'WAITING_EXTERNAL' | 'COMPLETED' | 'ESCALATED' | 'CANCELLED';
  priority: string | null;
  ownerId: string | null;
  owner?: CoordinationOwner | null;
  dueAt: string | null;
  causedBy: string | null;
  closeCriteria: string | null;
  metadata: CoordinationTaskMetadata;
};

export type CoordinationReferral = {
  id: string;
  episodeId: string | null;
  serviceType: string;
  reason: string | null;
  targetOrganization: string | null;
  targetProvider: string | null;
  status: 'REQUESTED' | 'ACCEPTED' | 'SCHEDULED' | 'SEEN' | 'REPORT_RECEIVED' | 'COMPLETED' | 'DECLINED' | 'UNABLE_TO_SCHEDULE';
  ownerId: string | null;
  owner?: CoordinationOwner | null;
  dueAt: string | null;
  requestedAt: string;
};

export type CoordinationAlert = {
  id: string;
  episodeId: string | null;
  source: 'ADT' | 'MISSED_CHECKIN' | 'SYMPTOM' | 'LAB' | 'MANUAL';
  title: string;
  details: string | null;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  status: 'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED' | 'DISMISSED';
  ownerId: string | null;
  owner?: CoordinationOwner | null;
  dueAt: string | null;
  triggeredAt: string;
};

export type CoordinationCommunication = {
  id: string;
  episodeId: string | null;
  type:
    | 'HANDOFF'
    | 'OUTREACH'
    | 'CARE_TEAM_UPDATE'
    | 'ESCALATION'
    | 'CAREGIVER_DISCUSSION'
    | 'EXTERNAL_PROVIDER_CALLBACK';
  status: 'OPEN' | 'COMPLETED' | 'CANCELLED';
  title: string;
  summary: string | null;
  details: string | null;
  ownerId: string | null;
  owner?: CoordinationOwner | null;
  dueAt: string | null;
  happenedAt: string;
};

export type CoordinationWorklistItem = {
  id: string;
  kind: 'task' | 'referral' | 'alert';
  title: string;
  status: string;
  ownerId: string | null;
  dueAt: string | null;
  episodeId: string | null;
  priority: string | null;
};

export type CoordinationTimelineEntry = {
  id: string;
  at: string;
  type: 'episode' | 'plan' | 'task' | 'referral' | 'communication' | 'alert';
  title: string;
  detail: string;
  status: string | null;
  ownerId: string | null;
  owner: CoordinationOwner | null;
  dueAt: string | null;
  episodeId: string | null;
};

export type CoordinationWorkspaceResponse = {
  summary: CoordinationSummary;
  episodes: CoordinationEpisode[];
  careTeam: CoordinationTeamMember[];
  plans: CoordinationPlan[];
  tasks: CoordinationTask[];
  referrals: CoordinationReferral[];
  monitoring: {
    alerts: CoordinationAlert[];
  };
  handoffs: CoordinationCommunication[];
  communications: CoordinationCommunication[];
  worklist: CoordinationWorklistItem[];
  timeline: CoordinationTimelineEntry[];
};

export type CoordinationPlanningOptions = {
  internalUsers: CoordinationPlanningInternalUser[];
  externalUsers: CoordinationExternalUser[];
  medicalForms: CoordinationPlanningMedicalForm[];
  taskCatalog: CoordinationTaskCatalogEntry[];
};

export function formatCoordinationDate(value?: string | null) {
  if (!value) return '-';
  return new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

export function formatCoordinationDay(value?: string | null) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString([], { dateStyle: 'medium' });
}
